import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Mail, Phone, Trash2, Check, RefreshCw, Inbox } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, quotes: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | quote | contact | unread

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: s }] = await Promise.all([
        api.get("/admin/contacts"),
        api.get("/admin/stats"),
      ]);
      setContacts(c);
      setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("km_token");
        navigate("/admin/login");
        return;
      }
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("km_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const logout = () => {
    localStorage.removeItem("km_token");
    localStorage.removeItem("km_user");
    navigate("/admin/login");
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/admin/contacts/${id}/read`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć tę wiadomość?")) return;
    try {
      await api.delete(`/admin/contacts/${id}`);
      toast.success("Usunięto");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = contacts.filter((c) => {
    if (filter === "all") return true;
    if (filter === "unread") return !c.read;
    return c.kind === filter;
  });

  return (
    <main data-testid="admin-dashboard-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 sticky top-0 z-30 backdrop-blur-xl bg-black/70">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 border border-white/40 flex items-center justify-center font-serif-display">
              M
            </span>
            <div>
              <p className="font-serif-display text-lg leading-none">Kamieniarstwo Mrozek</p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-1">
                Panel administratora
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="btn-ghost !py-2.5 !px-4 text-[10px]"
              title="Odśwież"
            >
              <RefreshCw size={14} strokeWidth={1} /> Odśwież
            </button>
            <button data-testid="admin-logout" onClick={logout} className="btn-primary !py-2.5 !px-4 text-[10px]">
              <LogOut size={14} strokeWidth={1} /> Wyloguj
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-neutral-900 mb-10">
          <Stat label="Wszystkie zapytania" value={stats.total} testid="stat-total" />
          <Stat label="Nieprzeczytane" value={stats.unread} testid="stat-unread" highlight />
          <Stat label="Wyceny" value={stats.quotes} testid="stat-quotes" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { k: "all", label: "Wszystkie" },
            { k: "unread", label: "Nieprzeczytane" },
            { k: "quote", label: "Wyceny" },
            { k: "contact", label: "Kontakt" },
          ].map((f) => (
            <button
              key={f.k}
              data-testid={`admin-filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.25em] border transition-colors ${
                filter === f.k
                  ? "bg-white text-black border-white"
                  : "bg-transparent text-neutral-300 border-white/15 hover:border-white/50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-neutral-500 text-sm">Wczytywanie...</p>
        ) : filtered.length === 0 ? (
          <div className="hairline p-12 text-center bg-[#0a0a0a]">
            <Inbox size={32} strokeWidth={1} className="mx-auto text-neutral-600 mb-4" />
            <p className="text-neutral-400">Brak wiadomości w tej kategorii.</p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-neutral-900">
            {filtered.map((c) => (
              <li
                key={c.id}
                data-testid={`admin-contact-${c.id}`}
                className={`bg-[#0a0a0a] p-6 md:p-7 ${!c.read ? "border-l-2 border-l-[var(--accent)]" : ""}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                      <h3 className="font-serif-display text-white text-xl">{c.name}</h3>
                      <span
                        className={`text-[9px] uppercase tracking-[0.25em] px-2 py-1 border ${
                          c.kind === "quote"
                            ? "border-[var(--accent)] text-[var(--accent)]"
                            : "border-white/20 text-neutral-400"
                        }`}
                      >
                        {c.kind === "quote" ? "Wycena" : "Kontakt"}
                      </span>
                      {!c.read && (
                        <span className="text-[9px] uppercase tracking-[0.25em] px-2 py-1 bg-white text-black">
                          Nowe
                        </span>
                      )}
                    </div>
                    {c.subject && <p className="text-sm text-neutral-300">{c.subject}</p>}
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 whitespace-nowrap">
                    {new Date(c.created_at).toLocaleString("pl-PL")}
                  </p>
                </div>

                <div className="flex flex-wrap gap-4 text-xs text-neutral-300 mb-4">
                  <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:text-white">
                    <Mail size={12} strokeWidth={1} /> {c.email}
                  </a>
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="flex items-center gap-2 hover:text-white">
                      <Phone size={12} strokeWidth={1} /> {c.phone}
                    </a>
                  )}
                </div>

                <p className="text-neutral-300 leading-relaxed whitespace-pre-wrap text-sm border-l border-white/15 pl-4 italic">
                  {c.message}
                </p>

                <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-white/10">
                  {!c.read && (
                    <button
                      data-testid={`admin-mark-read-${c.id}`}
                      onClick={() => markRead(c.id)}
                      className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 hover:text-white flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-white/40"
                    >
                      <Check size={12} strokeWidth={1} /> Oznacz jako przeczytane
                    </button>
                  )}
                  <button
                    data-testid={`admin-delete-${c.id}`}
                    onClick={() => remove(c.id)}
                    className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 hover:text-red-400 flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-red-400/50"
                  >
                    <Trash2 size={12} strokeWidth={1} /> Usuń
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}

const Stat = ({ label, value, testid, highlight }) => (
  <div data-testid={testid} className={`bg-[#0a0a0a] p-7 ${highlight ? "border-l-2 border-l-[var(--accent)]" : ""}`}>
    <p className="overline text-neutral-400 mb-3">{label}</p>
    <p className="font-serif-display text-white text-5xl">{value}</p>
  </div>
);
